import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, CalendarClock, AlertTriangle, AlertCircle, CheckCircle, Percent } from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import ReportFilters from './ReportFilters';


const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const BUCKET_META = {
  expired:  { label: 'Expired',            icon: AlertCircle,   color: 'text-red-500',    bg: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  critical: { label: 'Critical (≤30 days)',icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  soon:     { label: 'Renew soon (31-90d)',icon: CalendarClock, color: 'text-amber-500',  bg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  distant:  { label: 'Comfortable (>90d)', icon: CheckCircle,   color: 'text-green-500',  bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
};


export default function CorporateRenewalsReport() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [daysAhead, setDaysAhead] = useState(180);
  const [bucketFilter, setBucketFilter] = useState('all');
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const resp = await apiFetch(`engage/analytics/corporate-renewals/?days_ahead=${daysAhead}`);
        if (resp?.status === 'fail') setError(resp.message || 'Failed to load report');
        setData(resp?.status === 'fail' ? null : resp);
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to load renewals');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysAhead, reloadTick]);

  // The API returns { buckets: { expired: [], critical: [], soon: [], distant: [] } }
  const buckets = data?.buckets || {};
  const visibleBuckets = bucketFilter === 'all' ? ['expired', 'critical', 'soon', 'distant'] : [bucketFilter];

  return (
    <div className="p-4 max-w-[1200px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/reports')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Reports
      </button>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white shadow-sm">
          <CalendarClock className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Corporate Renewals</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Contract expiry urgency buckets</p>
        </div>
      </div>

      <ReportFilters
        preset={preset} setPreset={setPreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        onReload={() => setReloadTick(t => t + 1)}
        extras={[
          { label: 'Look ahead', value: String(daysAhead), onChange: v => setDaysAhead(Number(v)), options: [
            { value: '30',  label: 'Next 30 days' },
            { value: '90',  label: 'Next 90 days' },
            { value: '180', label: 'Next 180 days' },
            { value: '365', label: 'Next 12 months' },
          ] },
          { label: 'Bucket', value: bucketFilter, onChange: setBucketFilter, options: [
            { value: 'all',      label: 'All buckets' },
            { value: 'expired',  label: 'Expired' },
            { value: 'critical', label: 'Critical (≤30d)' },
            { value: 'soon',     label: 'Renew soon (31-90d)' },
            { value: 'distant',  label: 'Comfortable (>90d)' },
          ] },
        ]}
      />

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {['expired', 'critical', 'soon', 'distant'].map(k => {
          const M = BUCKET_META[k];
          return (
            <div key={k} className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <M.icon className={cn('w-4 h-4', M.color)} />
                <span className="text-[10px] uppercase text-gray-500 font-medium">{M.label}</span>
              </div>
              <div className={cn('text-xl font-bold mt-1',
                k === 'expired' ? 'text-red-600 dark:text-red-400' :
                k === 'critical' ? 'text-orange-600 dark:text-orange-400' :
                k === 'soon' ? 'text-amber-600 dark:text-amber-400' :
                'text-green-600 dark:text-green-400')}>
                {(buckets[k] || []).length}
              </div>
            </div>
          );
        })}
      </div>

      {loading ? <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading…</div> : (
        visibleBuckets.map(k => {
          const rows = buckets[k] || [];
          if (rows.length === 0) return null;
          const M = BUCKET_META[k];
          return (
            <div key={k} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-1.5">
                <M.icon className={cn('w-4 h-4', M.color)} />
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{M.label}</h2>
                <span className="ml-auto text-[11px] text-gray-500">{rows.length}</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Company</th>
                    <th className="px-4 py-2 text-left font-medium">Contract end</th>
                    <th className="px-4 py-2 text-right font-medium">Days</th>
                    <th className="px-4 py-2 text-right font-medium">Discount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map(r => (
                    <tr key={r.corporate_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="px-4 py-2.5"><Link to={`/engage/corporate-accounts/${r.corporate_id}`} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">{r.company_name}</Link></td>
                      <td className="px-4 py-2.5 text-[12px] text-gray-600 dark:text-gray-300">{fmt(r.contract_end_date)}</td>
                      <td className="px-4 py-2.5 text-right"><span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', M.bg)}>{r.days_until_expiry < 0 ? `${-r.days_until_expiry}d ago` : `${r.days_until_expiry}d`}</span></td>
                      <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300"><Percent className="w-3 h-3 inline mr-0.5" />{r.discount_pct}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </div>
  );
}
